import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_CUSTOMER,
  ROUTE_CUSTOMERS,
  ROUTE_RECEIVABLES
} from '@klear/constants'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { DeleteButton } from '@klear/ui/components/delete-button'
import { KlearDate } from '@klear/date'
import {
  useCustomers,
  useCustomerMutations,
  useCustomerPurchaseOrderVolumesListAll,
  useCustomerOfferForecastsListAll,
  useCustomerOfferHistoryListAll
} from '@klear/customer-ui/use-customers'
import { offerForecastExpectedLabel } from '@klear/invoice-offer/offer-forecast'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import toNumber from 'lodash/toNumber'
import isString from 'lodash/isString'

import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams, GridReadyEvent, GridApi } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import constant from 'lodash/constant'
import ViewIcon from '@/icons/ViewIcon'
import EditIcon from '@/icons/EditIcon'
import AddIcon from '@/icons/AddIcon'
import ExportIcon from '@/icons/ExportIcon'
import DeleteIcon from '@/icons/DeleteIcon'
import { Button } from '@klear/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

function customerOffersPath(customerName: string): string {
  const params = new URLSearchParams({ q: customerName })
  return `/${ROUTE_RECEIVABLES}?${params.toString()}`
}

const Customers = () => {
  useDocumentTitle('Customers')
  const { user } = useAuth()
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>(DEFAULT_CURRENCY_CODE)

  const { customers, isLoading, error } = useCustomers()
  const {
    volumesByCustomerId,
    isLoading: volumesLoading,
    error: volumesError,
    availableCurrencies: poCurrencies
  } = useCustomerPurchaseOrderVolumesListAll({ currency: selectedCurrency })
  const {
    historyByCustomerId,
    isLoading: historyLoading,
    error: historyError,
    availableCurrencies: offerCurrencies
  } = useCustomerOfferHistoryListAll({ currency: selectedCurrency })
  const { forecastsByCustomerId, isLoading: forecastsLoading } = useCustomerOfferForecastsListAll()
  const { deleteCustomer, isDeleting } = useCustomerMutations()
  const agGrid = useKlearAgGrid()

  const availableCurrencies = useMemo(
    () => sortBy(uniq([...poCurrencies, ...offerCurrencies])),
    [poCurrencies, offerCurrencies]
  )
  const showCurrencySelector = availableCurrencies.length > 1

  useEffect(() => {
    if (availableCurrencies.length === 0) return
    if (!availableCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(
        availableCurrencies.includes(DEFAULT_CURRENCY_CODE) ? DEFAULT_CURRENCY_CODE : availableCurrencies[0]!
      )
    }
  }, [availableCurrencies, selectedCurrency])

  const OfferForecastCellRenderer = useCallback(
    (props: ICellRendererParams) => {
      const customerId = props.data?.id
      const forecast = customerId == null ? undefined : forecastsByCustomerId.get(customerId)
      if (forecast == null) {
        return <span className="text-muted-foreground">—</span>
      }

      let label: string | null = null
      if (forecast.median != null) {
        label = offerForecastExpectedLabel(forecast.median)
      } else if (forecast.currentOverdueDays != null) {
        label = `Currently ${forecast.currentOverdueDays}d overdue`
      }
      if (label == null) {
        return <span className="text-muted-foreground">—</span>
      }

      return <span>{label}</span>
    },
    [forecastsByCustomerId]
  )

  const ActionCellRenderer = useCallback(
    (props: ICellRendererParams) => {
      const customer = props.data
      const customerId = customer?.id

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/${ROUTE_CUSTOMERS}/${customerId}`} className="flex items-center gap-2">
                <ViewIcon />
                View
              </Link>
            </DropdownMenuItem>
            {user.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_EDIT) && (
              <DropdownMenuItem asChild>
                <Link to={`/${ROUTE_CUSTOMERS}/${customerId}/edit`} className="flex items-center gap-2">
                  <EditIcon />
                  Edit
                </Link>
              </DropdownMenuItem>
            )}
            {user.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_DELETE) && customerId != null && (
              <DeleteButton
                resourceId={customerId}
                resourceName={customer.name}
                resourceType={PERMISSION_ENTITY_CUSTOMER}
                deleteMutation={(params: { id: number }) =>
                  deleteCustomer({
                    id: params.id,
                    traderId: customer.traderId
                  })
                }
                isDeleting={isDeleting}
                wrapperComponent={DropdownMenuItem}
              >
                <span className="flex items-center gap-2 text-red-600">
                  <DeleteIcon />
                  Delete
                </span>
              </DeleteButton>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    [user, deleteCustomer, isDeleting]
  )

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Organization Name',
        field: 'name',
        pinned: 'left',
        lockPosition: true,
        minWidth: 200,
        colId: 'name'
      },
      {
        headerName: 'Trader',
        field: 'traderName'
      },
      {
        headerName: 'Country',
        field: 'country',
        minWidth: 100,
        maxWidth: 120,
        flex: 0
      },
      {
        headerName: 'Created Date',
        field: 'createdAt',
        type: 'klearDate'
      },
      {
        headerName: 'Offers',
        colId: 'offerCount',
        sortable: true,
        filter: 'agNumberColumnFilter',
        cellDataType: 'number',
        minWidth: 90,
        maxWidth: 110,
        flex: 0,
        valueGetter: ({ data }) => (data && historyByCustomerId.get(data.id)?.offerCount) ?? 0,
        comparator: (a: number, b: number) => (toNumber(a) || 0) - (toNumber(b) || 0),
        cellRenderer: (params: ICellRendererParams) => {
          const count = params.data && historyByCustomerId.get(params.data.id)?.offerCount
          if (count == null || count <= 0) {
            return <span className="text-muted-foreground">—</span>
          }
          const customerName = isString(params.data?.name) ? params.data.name.trim() : ''
          if (!customerName) {
            return <span>{count}</span>
          }
          return (
            <Link
              to={customerOffersPath(customerName)}
              className="text-foreground hover:text-foreground underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {count}
            </Link>
          )
        }
      },
      {
        headerName: 'Purchase Order Volume',
        colId: 'purchaseOrderVolume',
        type: 'money',
        valueGetter: ({ data }) => (data && volumesByCustomerId.get(data.id)) ?? null
      },
      {
        headerName: 'Invoice Value',
        colId: 'invoiceValue',
        type: 'money',
        valueGetter: ({ data }) => (data && historyByCustomerId.get(data.id)?.invoiceValue) ?? null
      },
      {
        headerName: 'Funded Value',
        colId: 'fundedValue',
        type: 'money',
        valueGetter: ({ data }) => (data && historyByCustomerId.get(data.id)?.fundedValue) ?? null
      },
      {
        headerName: 'Payment Behavior',
        colId: 'offerForecast',
        sortable: true,
        filter: false,
        cellRenderer: OfferForecastCellRenderer,
        minWidth: 180,
        valueGetter: ({ data }) => (data && forecastsByCustomerId.get(data.id)?.median) ?? null
      },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        cellRenderer: ActionCellRenderer,
        pinned: 'right',
        lockPosition: true,
        menuTabs: [],
        valueGetter: constant(null),
        cellStyle: { overflow: 'visible' },
        flex: 0,
        width: 60,
        minWidth: 60,
        maxWidth: 60
      }
    ],
    [ActionCellRenderer, OfferForecastCellRenderer, volumesByCustomerId, historyByCustomerId, forecastsByCustomerId]
  )

  const defaultColDef = useMemo(
    () => ({
      ...agGrid.defaultColDef,
      flex: 1,
      minWidth: 150
    }),
    [agGrid.defaultColDef]
  )

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  const onFilterTextBoxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', e.target.value)
    }
  }

  const handleExport = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: `customers_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: [
          'name',
          'country',
          'createdAt',
          'offerCount',
          'purchaseOrderVolume',
          'invoiceValue',
          'fundedValue',
          'offerForecast'
        ]
      })
    }
  }

  const tableLoading = isLoading || volumesLoading || historyLoading || forecastsLoading

  useEffect(() => {
    if (!gridApi) return
    if (tableLoading) {
      gridApi.showLoadingOverlay()
      return
    }
    if (customers && customers.length > 0) {
      gridApi.hideOverlay()
    } else {
      gridApi.showNoRowsOverlay()
    }
  }, [gridApi, tableLoading, customers])

  useEffect(() => {
    if (!gridApi || volumesLoading || historyLoading || forecastsLoading) return
    gridApi.refreshCells({
      columns: ['purchaseOrderVolume', 'offerCount', 'invoiceValue', 'fundedValue', 'offerForecast'],
      force: true
    })
  }, [
    gridApi,
    volumesByCustomerId,
    historyByCustomerId,
    forecastsByCustomerId,
    volumesLoading,
    historyLoading,
    forecastsLoading
  ])

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">Error: {error.message}</div>
  }

  if (volumesError) {
    return (
      <div className="flex justify-center items-center h-64 text-red-500">
        Error loading purchase order volumes: {volumesError.message}
      </div>
    )
  }

  if (historyError) {
    return (
      <div className="flex justify-center items-center h-64 text-red-500">
        Error loading offer history: {historyError.message}
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Customers</h1>
        <div className="flex items-center gap-3">
          {showCurrencySelector && (
            <>
              <span className="text-sm text-muted-foreground">Currency</span>
              <CurrencyReportSelector
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                currencies={availableCurrencies}
              />
            </>
          )}
          {user.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_ADD) && (
            <Link
              to={`/${ROUTE_CUSTOMERS}/new`}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
            >
              <AddIcon />
              <span>Create Customer</span>
            </Link>
          )}
        </div>
      </div>

      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search customers by name"
          onChange={onFilterTextBoxChanged}
          className="flex-grow px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
          disabled={!gridApi || !customers || customers.length === 0}
        >
          <ExportIcon />
          Export CSV
        </button>
      </div>

      {!customers || customers.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No Customers found matching your search criteria.</p>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="w-full h-full min-h-[600px]">
            <AgGridReact
              rowData={customers}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              columnTypes={agGrid.columnTypes}
              dataTypeDefinitions={agGrid.dataTypeDefinitions}
              animateRows={true}
              rowSelection="multiple"
              pagination={true}
              paginationPageSize={20}
              theme={agGrid.theme}
              onGridReady={onGridReady}
              quickFilterText=""
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Customers
